import { handleApiError } from "../authGuards";
import type { PrototypeState } from "../controllerState";
import { setActiveTab, showView } from "../navigationController";
import type { Root } from "../prototypeTypes";
import { applyBrowseGridFilter, refreshBrowseGrid, setPendingBrowseFilter, setPendingBrowseIntent } from "../browseController";
import type { BrowseSort } from '../browseData';
import { DEFAULT_BROWSE_SORT } from '../browseData';
import { createBrowseHistoryEntryState } from '../browsePaginationSessionStore';
import { closeBrowseIndustryMenu } from '../browseIndustryMenu';

export async function activateBrowseFilter(root: Root, state: PrototypeState, filter: string, sort?: BrowseSort) {
  // Set a one-shot capsule override so that when showView("home-browse")
  // triggers refreshBrowseGrid, it lands on the requested capsule
  // (favorites / recent) instead of the default. This avoids a double
  // fetch: refreshBrowseGrid consumes the override and performs the
  // single server request for the right capsule. We do NOT call
  // applyBrowseGridFilter here — that would race with refreshBrowseGrid.
	if (sort === 'trending' || sort === 'newest') {
		clearPreviousBrowseIntent(root);
		setPendingBrowseIntent(filter, sort);
		showViewAndTab(root, 'home-browse', filter, sort);
		return;
  }
  setPendingBrowseFilter(filter);
  showViewAndTab(root, 'home-browse', filter);
}

function clearPreviousBrowseIntent(root: Root): void {
	const search = root.querySelector<HTMLInputElement>('#browseSearchInput');
	if (search) search.value = '';
	const clear = root.querySelector<HTMLElement>('#browseSearchClear');
	if (clear) clear.style.display = 'none';
	const holder = root as unknown as { __browseFilter?: string; __browseSort?: BrowseSort };
	delete holder.__browseFilter;
	delete holder.__browseSort;
}

/**
 * Filter action handler. Each browse capsule triggers a server-side
 * API request for its own filtered data. No client-side filtering
 * of a pre-loaded catalog.
 */
export async function filterBrowse(root: Root, source: HTMLElement, state: PrototypeState, filter: string) {
	// Live category controls intentionally share the stable `filterBrowse('all')`
	// action contract. Their category is data, not executable action text. Always
	// prefer the element-owned slug so desktop and menu controls cannot silently
	// collapse to All after hydration.
	const sourceCategory = source.dataset.browseCategory?.trim().toLowerCase();
	const selectedFilter = sourceCategory && /^[a-z0-9]+(?:-[a-z0-9]+)*$/u.test(sourceCategory) ? sourceCategory : filter;
  // Highlight the active pill
  const row = source.closest<HTMLElement>("#browseFilters");
  row?.querySelectorAll(".category-pill").forEach((node) => node.classList.remove("active"));
  source.classList.add("active");

	applyBrowseGridFilter(root, selectedFilter);
	if (source.getAttribute('role') === 'option') closeBrowseIndustryMenu(root, { restoreFocus: true, suppressFocusRing: true });
}

export async function markFavoriteCards(root: Root, state: PrototypeState, opts: { favorite?: { topicId: string; favorited: boolean } } = {}) {
	if (opts.favorite) {
		root.querySelectorAll<HTMLElement>('.topic-card[data-id]').forEach((card) => {
			if (card.dataset.id !== opts.favorite!.topicId) return;
			card.toggleAttribute('data-favorited', opts.favorite!.favorited);
			card.querySelector<HTMLElement>('.fav-btn')?.classList.toggle('active', opts.favorite!.favorited);
		});
		return;
	}
  try {
    const response = await state.runtime.api.listFavorites();
    const favoriteTimes = new Map((response.favorites ?? []).map((item) => [item.topicSlug, item.starredAt]));
    const favoriteSlugs = new Set(favoriteTimes.keys());
    root.querySelectorAll<HTMLElement>("#browseGrid .topic-card").forEach((card) => {
      const id = card.dataset.id ?? "";
      const favorited = favoriteSlugs.has(id);
      card.toggleAttribute("data-favorited", favorited);
      const favoritedAt = favoriteTimes.get(id);
      if (favoritedAt) card.dataset.favoritedAt = favoritedAt;
      else delete card.dataset.favoritedAt;
      card.querySelector<HTMLElement>(".fav-btn")?.classList.toggle("active", favorited);
    });
  } catch (error) {
    handleApiError(state.runtime, state.effectiveAccessPlan, state.isLoggedIn, error, { root, intent: "favorite" });
  }
}

function showViewAndTab(root: Root, view: string, filter?: string, sort?: BrowseSort) {
	if (view === 'home-browse' && typeof window !== 'undefined') {
		const target = filter ? `/browse?category=${encodeURIComponent(filter)}&sort=${sort === 'trending' ? 'trending' : DEFAULT_BROWSE_SORT}` : '/browse';
		if (`${window.location.pathname}${window.location.search}` !== target) window.history.pushState(createBrowseHistoryEntryState(window.history.state), '', target);
		showView(view, root, { historyMode: 'none' });
		setActiveTab(view, root);
		return;
	}
  showView(view, root);
  setActiveTab(view, root);
}
